import React, { useState, useEffect } from 'react';
import { X, Zap, Plus, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOverlayState } from '../context/OverlayContext';
import { tokenTopUpProduct, createCheckoutSession } from '../stripe-config';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { checkVybeLimits, VybeLimits } from '../utils/vybeLimits';

interface TokenTopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason?: string;
  onStartVybe?: () => void;
}

const TokenTopUpModal: React.FC<TokenTopUpModalProps> = ({ isOpen, onClose, reason, onStartVybe }) => {
  useOverlayState(isOpen, 'token-topup-modal');
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();
  const [limits, setLimits] = useState<VybeLimits | null>(null);

  const isSubscribed = currentUser?.subscriptionTier === 'subscribed';

  useEffect(() => {
    const fetchLimits = async () => {
      if (!currentUser || !isOpen) return;
      const vybeLimits = await checkVybeLimits(currentUser.id, isSubscribed);
      setLimits(vybeLimits);
    };
    fetchLimits();
  }, [currentUser, isSubscribed, isOpen]);

  const handlePurchase = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to purchase credits');
        return;
      }

      const result = await createCheckoutSession(
        tokenTopUpProduct.priceId,
        'payment',
        `${window.location.origin}/payment-success?type=token_topup`,
        `${window.location.origin}/payment-cancelled`,
        undefined,
        session.access_token,
        { type: 'token_topup' }
      );

      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start purchase process');
    } finally {
      setIsLoading(false);
    }
  };

  const hasNoHostCredits = limits && limits.create.remaining === 0;
  const canStartVybe = limits && limits.create.remaining >= 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-br from-green-500/20 via-emerald-500/20 to-teal-500/20 p-6">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white/70 hover:text-white transition"
                >
                  <X size={24} />
                </button>

                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center">
                    <Zap size={32} className="text-white" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white text-center mb-2">
                  Vybe Credits
                </h2>
                <p className="text-white/70 text-center text-sm">
                  {reason || "Here's what you can do this month"}
                </p>
              </div>

              <div className="p-6 space-y-4">
                {limits ? (
                  <>
                    <div className="mb-4">
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className={`bg-gradient-to-br ${
                          limits.create.remaining === 0
                            ? 'from-red-500/10 to-red-600/5 border-red-500/30'
                            : 'from-green-500/10 to-green-600/5 border-green-500/30'
                        } border rounded-xl p-6 text-center`}
                      >
                        <div className="flex justify-center mb-3">
                          <div className={`p-3 rounded-lg ${
                            limits.create.remaining === 0 ? 'bg-red-500/20' : 'bg-green-500/20'
                          }`}>
                            <Sparkles size={24} className={limits.create.remaining === 0 ? 'text-red-400' : 'text-green-400'} />
                          </div>
                        </div>
                        <div className={`text-4xl font-bold mb-2 ${
                          limits.create.remaining === 0 ? 'text-red-400' : 'text-white'
                        }`}>
                          {limits.create.remaining}
                        </div>
                        <div className="text-sm text-white/70">
                          Vybes You Can Host
                        </div>
                        {limits.create.remaining === 0 && (
                          <div className="text-xs text-red-400 mt-3">
                            Out of Host credits this month
                          </div>
                        )}
                      </motion.div>
                    </div>

                    <div className="text-center py-2">
                      <p className="text-xs text-white/50">
                        {limits.create.tokens > 0
                          ? 'Includes bonus credits from top-ups'
                          : 'Resets monthly on the 1st'}
                      </p>
                    </div>

                    {canStartVybe && onStartVybe ? (
                      <button
                        onClick={() => {
                          onClose();
                          onStartVybe();
                        }}
                        className="w-full bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-green-500/50 transition-all"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles size={20} />
                          <span>Start a Vybe</span>
                        </div>
                      </button>
                    ) : hasNoHostCredits ? (
                      <button
                        onClick={handlePurchase}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 text-white font-bold py-4 rounded-xl hover:shadow-lg hover:shadow-green-500/50 transition-all disabled:opacity-50"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Plus size={20} />
                          <span>{isLoading ? 'Processing...' : 'Top Up Credits'}</span>
                        </div>
                      </button>
                    ) : null}

                    {hasNoHostCredits && (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <p className="text-white/90 text-sm text-center mb-3">
                          Get {tokenTopUpProduct.tokens.create} more Host credit{tokenTopUpProduct.tokens.create > 1 ? 's' : ''} for £{tokenTopUpProduct.price}
                        </p>
                        <div className="text-center">
                          <div className="inline-flex items-center gap-2 bg-green-500/20 px-4 py-2 rounded-lg">
                            <Sparkles size={16} className="text-green-400" />
                            <span className="font-bold text-green-400 text-lg">+{tokenTopUpProduct.tokens.create}</span>
                            <span className="text-white/70 text-sm">Host Credit{tokenTopUpProduct.tokens.create > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full mx-auto" />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TokenTopUpModal;
